import { useState, useEffect } from "react";
import { Button, FormInput, FormSelect, Modal, DataTable } from "../../../components/ui";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import { TableActions } from "../../../components/ui/TableActions";
import { TABLE_BACKGROUNDS } from "../../../constants/tableBackgrounds";
import {
  getPasswords,
  createPassword,
  updatePassword,
  disablePassword,
} from "../services/ownerSecurityService";
import toast from "react-hot-toast";
import { Eye, EyeOff, Plus, Trash2, Lock, Loader2 } from "lucide-react";

const CUSTOM_FIELD_OPTIONS = [
  { value: "IMEI Number 1", label: "IMEI Number 1" },
  { value: "IMEI Number 2", label: "IMEI Number 2" },
  { value: "Phone Number", label: "Phone Number" },
  { value: "Aadhar", label: "Aadhar" },
  { value: "PAN", label: "PAN" },
  { value: "Security Question", label: "Security Question" },
  { value: "PIN", label: "PIN" },
  { value: "Recovery Email", label: "Recovery Email" },
  { value: "Bank Account", label: "Bank Account" },
  { value: "Server IP", label: "Server IP" },
  { value: "IPv4 Address", label: "IPv4 Address" },
  { value: "IPv6 Address", label: "IPv6 Address" },
  { value: "Custom", label: "Custom (Type your own)" },
];

export default function OwnerSecurityComponent() {
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [isViewing, setIsViewing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCustomFields, setShowCustomFields] = useState({});
  const [search, setSearch] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    fetchPasswords();
  }, []);

  const fetchPasswords = async () => {
    try {
      setLoading(true);
      const res = await getPasswords();
      setPasswords(res.data?.data?.passwords || []);
    } catch (error) {
      toast.error("Failed to load passwords");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (entry = null, viewOnly = false) => {
    setCurrentEntry(
      entry || {
        title: "",
        website: "",
        username: "",
        password: "",
        notes: "",
        customFields: [],
      }
    );
    setIsViewing(viewOnly);
    setShowPassword(false);
    setShowCustomFields({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentEntry(null);
  };

  const handleSave = async () => {
    if (!currentEntry.title) {
      toast.error("Title is required");
      return;
    }

    // Validate email
    if (currentEntry.username && currentEntry.username.includes('@')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(currentEntry.username)) {
        toast.error("Invalid Email format");
        return;
      }
    }

    // Validate custom fields
    for (const field of currentEntry.customFields) {
      const { key, value } = field;
      if (!value) continue; // Skip empty values

      if (key === "Phone Number" && !/^\d{10}$/.test(value.replace(/\D/g, ''))) {
        toast.error("Phone Number must be 10 digits");
        return;
      }
      if ((key === "IMEI Number 1" || key === "IMEI Number 2") && !/^\d{15}$/.test(value)) {
        toast.error(`${key} must be 15 digits`);
        return;
      }
      if (key === "Aadhar" && !/^\d{12}$/.test(value.replace(/\s/g, ''))) {
        toast.error("Aadhar must be 12 digits");
        return;
      }
      if (key === "PAN" && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(value)) {
        toast.error("Invalid PAN format");
        return;
      }
      if (key === "IPv4 Address" && !/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(value)) {
        toast.error("Invalid IPv4 Address");
        return;
      }
      if (key === "IPv6 Address" && !value.includes(':')) {
        toast.error("Invalid IPv6 Address");
        return;
      }
    }

    try {
      if (currentEntry._id) {
        await updatePassword(currentEntry._id, currentEntry);
        toast.success("Entry updated");
      } else {
        await createPassword(currentEntry);
        toast.success("Entry created");
      }
      handleCloseModal();
      fetchPasswords();
    } catch (error) {
      toast.error("Failed to save entry");
    }
  };

  const confirmDelete = (entry) => {
    setSelectedEntry(entry);
    setDeleteModalOpen(true);
  };

  const handleToggle = async () => {
    if (!selectedEntry) return;
    const isCurrentlyActive = selectedEntry.isActive !== false;
    try {
      await disablePassword(selectedEntry._id);
      toast.success(isCurrentlyActive ? "Entry disabled" : "Entry enabled");
      fetchPasswords();
    } catch (error) {
      toast.error(`Failed to ${isCurrentlyActive ? 'disable' : 'enable'} entry`);
    } finally {
      setDeleteModalOpen(false);
      setSelectedEntry(null);
    }
  };

  const addCustomField = () => {
    setCurrentEntry((prev) => ({
      ...prev,
      customFields: [...prev.customFields, { key: "IMEI Number 1", value: "" }],
    }));
  };

  const updateCustomField = (index, field, val) => {
    const updated = [...currentEntry.customFields];
    updated[index][field] = val;
    setCurrentEntry((prev) => ({ ...prev, customFields: updated }));
  };

  const removeCustomField = (index) => {
    const updated = [...currentEntry.customFields];
    updated.splice(index, 1);
    setCurrentEntry((prev) => ({ ...prev, customFields: updated }));
  };

  const toggleCustomFieldVisibility = (index) => {
    setShowCustomFields((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const filteredPasswords = passwords.filter(item => 
    (item.title || "").toLowerCase().includes(search.toLowerCase()) || 
    (item.website || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.username || "").toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      header: "Title",
      render: (item) => <span className="text-slate-800 font-semibold text-xs">{item.title}</span>
    },
    {
      header: "Website / App",
      render: (item) => <span className="text-slate-600 text-[11px] font-medium">{item.website || "—"}</span>
    },
    {
      header: "Username / Email",
      render: (item) => <span className="text-slate-600 text-[11px] font-medium">{item.username || "—"}</span>
    },
    {
      header: "Created Date & Time",
      className: "w-40 text-center",
      render: (item) => <span className="text-[11px] font-semibold text-slate-600 whitespace-nowrap">{item.createdAt ? new Date(item.createdAt).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "—"}</span>
    },
    {
      header: "Actions",
      className: "text-center w-24",
      render: (item) => {
        const isActive = item.isActive !== false;
        return (
          <TableActions
            onView={isActive ? () => handleOpenModal(item, true) : null}
            onEdit={isActive ? () => handleOpenModal(item, false) : null}
            onToggle={() => confirmDelete(item)}
            isActive={isActive}
          />
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-slate-800 tracking-tight leading-none mb-2">
            Owner Security
          </h3>
          <p className="text-[13px] text-slate-500 font-medium">
            Securely manage passwords and sensitive information.
          </p>
        </div>
        <Button variant="orange" size="md" onClick={() => handleOpenModal()} leftIcon={<Plus size={16} />}>
          Add Entry
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries by title, website, username..."
            className="w-full h-9 pl-9 pr-4 text-xs bg-slate-50 border border-slate-200 rounded-md outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
          <svg className="absolute left-3 top-2.5 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </div>
      </div>

      <div className="card overflow-hidden relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm z-10">
            <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
          </div>
        )}
        
        {!loading && passwords.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <Lock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>No secure entries found. Add your first one above.</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredPasswords}
            backgroundImage={TABLE_BACKGROUNDS.teams}
            isRowInactive={(row) => row.isActive === false}
            getRowClassName={(row) => row.isActive === false ? 'bg-slate-100/90 border-y border-slate-200/80 opacity-60' : 'hover:bg-slate-50/50'}
          />
        )}
      </div>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={isViewing ? "View Secure Entry" : currentEntry?._id ? "Edit Secure Entry" : "New Secure Entry"}
          size="lg"
        >
          <div className="space-y-4">
            <FormInput
              label="Title"
              value={currentEntry.title}
              onChange={(e) =>
                setCurrentEntry({ ...currentEntry, title: e.target.value })
              }
              placeholder="e.g. Apple ID, Bank Login"
              required
              disabled={isViewing}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Website / App"
                value={currentEntry.website}
                onChange={(e) =>
                  setCurrentEntry({ ...currentEntry, website: e.target.value })
                }
                placeholder="e.g. apple.com"
                disabled={isViewing}
              />
              <FormInput
                label="Username / Email"
                value={currentEntry.username}
                onChange={(e) =>
                  setCurrentEntry({ ...currentEntry, username: e.target.value })
                }
                disabled={isViewing}
              />
            </div>

            <div className="relative">
              <FormInput
                label="Password"
                type={showPassword ? "text" : "password"}
                value={currentEntry.password}
                onChange={(e) =>
                  setCurrentEntry({ ...currentEntry, password: e.target.value })
                }
                disabled={isViewing}
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-slate-400 hover:text-brand-blue"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>

            <div className="relative">
              <FormInput
                label="Notes"
                type="text"
                value={currentEntry.notes}
                onChange={(e) =>
                  setCurrentEntry({ ...currentEntry, notes: e.target.value })
                }
                disabled={isViewing}
              />
            </div>

            {/* Custom Fields Section */}
            <div className="pt-4 border-t border-slate-100">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-bold text-slate-700">
                  Custom Fields
                </h4>
                {!isViewing && (
                  <Button variant="secondary" size="xs" onClick={addCustomField}>
                    <Plus className="w-3 h-3 mr-1" /> Add Field
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {currentEntry.customFields.map((field, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="w-1/3">
                      <FormSelect
                        value={CUSTOM_FIELD_OPTIONS.some((o) => o.value === field.key) ? field.key : "Custom"}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateCustomField(idx, "key", val === "Custom" ? "" : val);
                        }}
                        options={CUSTOM_FIELD_OPTIONS}
                        disabled={isViewing}
                      />
                      {!CUSTOM_FIELD_OPTIONS.some((o) => o.value === field.key) && (
                        <input
                          className="mt-2 w-full text-sm border-slate-200 rounded-md focus:border-brand-blue focus:ring-brand-blue py-2 px-3 border disabled:bg-slate-50 disabled:text-slate-500"
                          placeholder="Type Custom Key"
                          value={field.key}
                          onChange={(e) => updateCustomField(idx, "key", e.target.value)}
                          disabled={isViewing}
                        />
                      )}
                    </div>
                    <div className="flex-1 relative">
                      <FormInput
                        type={showCustomFields[idx] ? "text" : "password"}
                        value={field.value}
                        onChange={(e) =>
                          updateCustomField(idx, "value", e.target.value)
                        }
                        placeholder="Value"
                        disabled={isViewing}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-3 text-slate-400 hover:text-brand-blue"
                        onClick={() => toggleCustomFieldVisibility(idx)}
                      >
                        {showCustomFields[idx] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {!isViewing && (
                      <button
                        className="mt-2 p-2 text-slate-400 hover:text-red-500"
                        onClick={() => removeCustomField(idx)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            {isViewing ? (
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                  Save Entry
                </Button>
              </>
            )}
          </div>
        </Modal>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedEntry(null);
        }}
        onConfirm={handleToggle}
        title={selectedEntry?.isActive === false ? "Enable Secure Entry" : "Disable Secure Entry"}
        message={
          selectedEntry?.isActive === false 
            ? "Are you sure you want to enable this secure entry? It will become active and editable again."
            : "Are you sure you want to disable this secure entry? This will hide it from view/edit until enabled again."
        }
        variant={selectedEntry?.isActive === false ? "success" : "danger"}
        confirmLabel={selectedEntry?.isActive === false ? "Enable" : "Disable"}
      />
    </div>
  );
}
